/**
 * External dependencies
 */

/**
 * React dependencies
 */

import { useEffect, useState } from 'react';

/**
 * DWT dependencies
 */

/**
 * DWT components
 */

/*
 * DWT hooks
 */

/**
 * useFetch hook
 *
 * Custom hook to handle data both fetched from the API and passed down from a data attribute that was manually entered in the editor
 *
 * @param {Object} context
 * @param {Array}  endpoint - list of paths to map through
 */
const useFetchPRHData = ( context, endpoint ) => {
  const [ loading, setLoading ] = useState( false );
  const [ error, setError ] = useState( false );

  const [ recordCount, setRecordCount ] = useState( [] );
  const [ data, setData ] = useState( [] );
  const [ start, setStart ] = useState( 0 );

  const controller = new AbortController();
  const signal = controller.signal;

  const setupRequest = async( rows ) => {
    const baseUrl = window.location.origin;
    const body = JSON.stringify( setupRequestParams( rows ) );
    const ajaxNonce = typeof ajax_object !== 'undefined' ? ajaxNonce : null;

    const response = await fetch( `${baseUrl}${endpoint}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        // eslint-disable-next-line camelcase, no-undef
        'X-WP-Header': ajaxNonce
      },
      body,
      signal
    });

    const responseData = await response.json();
    return responseData;
  };

  const setupRequestParams = ( rows ) => {
    const dir = context.filterBy === 'showComingSoon' ? 'asc' : 'desc';
    const formats = context?.formatCodes;
    const formatArr = formats?.split( ',' );
    const unique = [ ...new Set( formatArr ) ];
    const format = unique.toString();

    const childrensCats = context?.childrensCategories?.split( ',' ).filter( ( cat ) => cat !== '' ).toString();

    const englishOnly = context?.languageCodes?.includes( 'e' ) && !context?.languageCodes?.includes( 'sp' );
    const spanishOnly = context?.languageCodes?.includes( 'sp' ) && !context?.languageCodes?.includes( 'e' );
    const languageCode = englishOnly ? 'E' : spanishOnly ? 'SP' : null;
    const bisac = context?.bisacCode ? context.bisacCode : context?.categoryCode ? context.categoryCode : null;

    if ( context?.filterBy === 'brand' ) {
      delete context.filterBy;
    }

    const dataFromEditor = {
      ageRange: context?.ageRangeCode ?? null,
      authorLastInitial: context?.authorLastInitial ?? null,
      catSetId: bisac ? 'BI' : null,
      catUri: bisac ?? null,
      childrensBookCategory: childrensCats ?? null,
      contribRoleCode: context?.contribRoleCode ?? null,
      dir: context?.direction ?? dir,
      divisionCode: context?.divisionCode ?? null,
      filterBy: context?.filterBy ?? null,
      format: context?.formatCodes ? format : null,
      hideAuthorsWithNoPhoto: context?.hideAuthorsWithNoPhoto ?? null,
      imprintCode: context?.imprintCode ?? null,
      isbn: context?.collectionIsbns ?? null,
      language: languageCode,
      productLine: context?.brandCode ?? null,
      rhcbTheme: context?.themeCode ?? null,
      rows,
      seriesCode: context?.seriesCode ?? null,
      showCovers: context?.showCovers ?? null,
      sort: context?.sortBy ?? null,
      start
    };

    const filterOutNull = Object.entries( dataFromEditor )
      .filter( ( [ value ] ) => value !== null )
      .reduce( ( obj, [ key, value ] ) => {
        obj[key] = value;
        return obj;
      }, {});

    return filterOutNull;
  };

  const makeRequest = () => {
    const rows = context.listType === 'full-list' ? 24 : 12;

    setLoading( true );
    setupRequest( rows )
      .then( ( response ) => {
        const responseData = context.featureBy === 'books' ? response.data.works : response.data.authors;

        start === 0 ? setData( responseData ) : setData( [ ...data, ...responseData ] );
        setRecordCount( response.recordCount );

        response.code === 'no_data_found' || response.code === 'internal_server_error' ? setError( true ) : setError( false );
      })
      .catch( ( error ) => {
        console.error( error );
        setError( true );
      })
      .finally( () => {
        setLoading( false );
        setStart( ( prevStart ) => prevStart + rows );
      });
  };

  useEffect( () => {
    if ( context.entry === 'automatic' ) {
      makeRequest();
    }

    if ( context.entry === 'manual' && context.manualData !== undefined ) {
      setData( JSON?.parse( context.manualData ) );
    }
  }, [ context ] );

  return {
    makeRequest,
    setStart,
    recordCount,
    loading,
    data,
    error
  };
};

export default useFetchPRHData;
